// pages/devolucionesVenta/DetalleDevolucionVentaModal.tsx
import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper,
  IconButton, Box, Button, Stack, Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  AssignmentReturn as ReturnIcon,
  CalendarToday as CalendarIcon,
  Inventory2 as ArticleIcon,
} from '@mui/icons-material';
import { IDevolucionVenta } from '../../services/api/devolucionVentaApi';

// ── Paleta compartida con la lista ──
const P = {
  light: {
    bg: '#FFFFFF', bgS: '#F7F7F5', bgT: '#F1F1EF', text: '#37352F',
    textS: '#6B6B6B', textT: '#9B9A97', border: '#E9E9E7',
    success: '#0F7B6C', successBg: '#DBEDDB',
    accent: '#2383E2', accentBg: '#E8F0FE',
  },
  dark: {
    bg: '#191919', bgS: '#202020', bgT: '#2B2B2B', text: '#E8E8E6',
    textS: '#9B9A97', textT: '#6B6B6B', border: '#333333',
    success: '#4DAB9A', successBg: '#1A2F2A',
    accent: '#529CCA', accentBg: '#143A52',
  },
};

interface Props {
  open: boolean;
  onClose: () => void;
  devolucion: IDevolucionVenta | null;
  dark?: boolean;
}

export const DetalleDevolucionVentaModal: React.FC<Props> = ({
  open, onClose, devolucion, dark = false,
}) => {
  const p = dark ? P.dark : P.light;

  const cellSx = {
    borderBottom: `1px solid ${p.border}`,
    color: p.text,
    fontSize: '0.875rem',
    py: 1.25,
  };
  const headCellSx = {
    ...cellSx,
    color: p.textT,
    fontWeight: 600,
    fontSize: '0.72rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    bgcolor: p.bgS,
  };

  const totalArticulos = devolucion?.detalles?.reduce((s, d) => s + d.Cantidad, 0) ?? 0;
  const fechaDevolucion = devolucion?.detalles?.[0]?.FechaDevolucion
    ? new Date(devolucion.detalles[0].FechaDevolucion).toLocaleDateString('es-CO', {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : '—';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: p.bg,
          color: p.text,
          borderRadius: '12px',
          border: `1px solid ${p.border}`,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.5, borderBottom: `1px solid ${p.border}` }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{
              width: 34, height: 34, borderRadius: '8px',
              bgcolor: p.accentBg, display: 'flex', alignItems: 'center',
              justifyContent: 'center', color: p.accent, '& svg': { fontSize: 18 },
            }}>
              <ReturnIcon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: p.text, lineHeight: 1.2 }}>
                Devolución #{devolucion?.Id}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: p.textS, mt: 0.25 }}>
                Factura {devolucion?.CodigoFacturaVenta}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" sx={{ color: p.textT }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5, px: 3 }}>
        {/* ── Resumen ── */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Box sx={{ p: 2, bgcolor: p.bgS, borderRadius: '8px', border: `1px solid ${p.border}` }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ReturnIcon sx={{ fontSize: 16, color: p.textT }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: p.textT, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Código Factura
                </Typography>
              </Stack>
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: p.text, mt: 0.75 }}>
                {devolucion?.CodigoFacturaVenta ?? '—'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ p: 2, bgcolor: p.bgS, borderRadius: '8px', border: `1px solid ${p.border}` }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ArticleIcon sx={{ fontSize: 16, color: p.textT }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: p.textT, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Artículos devueltos
                </Typography>
              </Stack>
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: p.text, mt: 0.75 }}>
                {totalArticulos} unidad{totalArticulos !== 1 ? 'es' : ''}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ p: 2, bgcolor: p.bgS, borderRadius: '8px', border: `1px solid ${p.border}` }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarIcon sx={{ fontSize: 16, color: p.textT }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: p.textT, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Fecha
                </Typography>
              </Stack>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: p.text, mt: 0.75 }}>
                {fechaDevolucion}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* ── Tabla de artículos ── */}
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: p.textS, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Detalle de artículos
        </Typography>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: `1px solid ${p.border}`, borderRadius: '8px', bgcolor: p.bg }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Artículo</TableCell>
                <TableCell sx={headCellSx} align="center">Cantidad</TableCell>
                <TableCell sx={headCellSx}>Fecha</TableCell>
                <TableCell sx={headCellSx}>Observación</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devolucion?.detalles && devolucion.detalles.length > 0 ? (
                devolucion.detalles.map((detalle) => (
                  <TableRow
                    key={detalle.Id}
                    sx={{ '&:last-child td': { borderBottom: 'none' }, '&:hover': { bgcolor: p.bgS } }}
                  >
                    <TableCell sx={cellSx}>
                      <Stack spacing={0.25}>
                        <Typography sx={{ fontWeight: 600, color: p.text, fontSize: '0.875rem' }}>
                          {detalle.articulo?.Nombre ?? `Artículo #${detalle.IdArticulo}`}
                        </Typography>
                        {detalle.articulo?.EAN && (
                          <Typography sx={{ fontSize: '0.72rem', color: p.textT, fontFamily: 'monospace' }}>
                            {detalle.articulo.EAN}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={cellSx} align="center">
                      <Box sx={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 30, height: 30, borderRadius: '50%',
                        bgcolor: p.accentBg, color: p.accent,
                        fontSize: '0.85rem', fontWeight: 700,
                      }}>
                        {detalle.Cantidad}
                      </Box>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Typography sx={{ color: p.textS, fontSize: '0.85rem' }}>
                        {detalle.FechaDevolucion
                          ? new Date(detalle.FechaDevolucion).toLocaleDateString('es-CO')
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Typography sx={{
                        color: detalle.Observacion ? p.textS : p.textT,
                        fontSize: '0.85rem',
                        fontStyle: detalle.Observacion ? 'normal' : 'italic',
                      }}>
                        {detalle.Observacion || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ ...cellSx, py: 4 }}>
                    <Typography sx={{ color: p.textT, fontSize: '0.9rem' }}>
                      Sin artículos registrados
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${p.border}` }}>
        <Button
          onClick={onClose}
          size="small"
          sx={{
            color: p.textS, textTransform: 'none', fontWeight: 500,
            borderRadius: '7px', px: 2,
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetalleDevolucionVentaModal;
